/**
 * @module FileDocuments
 */

import { LoggerService, MainService } from '../services/core';
import { FileDescriptor, FileViewer } from './common';
import { Subject, Subscription, filter } from 'rxjs';
import { Bubble, RBEvent, User } from '.';
import { ContactService } from '../services/core/contact.service';
import { FILEDOCUMENT_SVC, FileDocumentService } from '../services/fileDocument/fileDocument.service';

export enum FileDocumentState {
    DELETED = "deleted",
    UPLOADING = "uploading",
    UPLOADED = "uploaded",
    NOT_UPLOADED = "not_uploaded",
    DOWNLOADING = "downloading",
    UPLOADERROR = "uploadError",
    UNKNOWN = "unknown"
}

export enum FileDocumentSearchMime {
    IMAGE = 'image',
    AUDIO_VIDEO = 'audio-video',
    PDF = 'pdf',
    PRESENTATION = 'presentation',
    DOCUMENT = 'document',
    SPREADSHEET = 'spreadsheet'
}

export enum FileDocumentSearchSet {
    ALL = "all",
    MINE = "mine",
    RECEIVED = "received"
}

export enum FileDocumentSearchSortBy {
    FILENAME = "fileName",
    UPLOADED_DATA = "uploadedDate",
    SIZE = "size"
}

export enum FileDocumentSearchSortDirection {
    TOP = 1,
    DOWN = -1
}

export interface FileDocumentThumbnail {
    /**
     *
     */
    possible: boolean;
    /**
     *
     */
    available: boolean;
    /**
     *
     */
    url(): Promise<string>;
}

export interface FileDocumentSearchOptions {

    /**
     * Only searches for files whose name contains this string
     */
    name?: string;

    /**
     * DocumentSet, define in which collection set the search should be executed ALL, MINE or RECEIVED
     * Default is ALL
     */
    documentSet?: FileDocumentSearchSet;

    /**
     * The document media type (also known as a Multipurpose Internet Mail Extensions or MIME type) indicates the nature and format
     * of a document. MIME types are defined and standardized in IETF's RFC 6838
     * Default is ''
     */
    mime?: FileDocumentSearchMime;

    /**
     * The search result will be sorted following the sortBy info
     * Default is UPLOADED_DATA
     */
    sortBy?: FileDocumentSearchSortBy;

    /**
     * The sort direction
     * Default is DOWN
     */
    sortDirection?: FileDocumentSearchSortDirection;
}

/**
 * @eventProperty
 */
export enum FileDocumentEvents {
    /**
     * @eventProperty
     * When a document is uploaded, the backend will automatically generate a thumbnail whenever possible
     * (this function is in place for images, video files and pdf files).
     * This event is fired once when the thumbnail generation is complete and the document is available for download.
     */
    ON_THUMBNAIL_AVAILABLE = 'ON_THUMBNAIL_AVAILABLE',

    /**
     * @eventProperty
     * This event is fired once when the upload is complete and the document is available for download.
     */
    ON_UPLOADED = 'ON_UPLOADED',

    /**
    * @eventProperty
    * When a large document is uploaded, it will be sent to the backend in chunks, making it possible to monitor its progress.
    * This event is fired when the transfer progress (in %) changes.
    */
    ON_TRANSFERT_IN_PROGRESS = 'ON_TRANSFERT_IN_PROGRESS',

    /**
    * @eventProperty
    * This event is fired when the content of the document is modified
    */
    ON_CONTENT_MODIFIED = 'ON_CONTENT_MODIFIED',
}

export class FileDocumentViewer {
    public id: string;
    public type?: string;

    static createFormFileViewer(fileViewer: FileViewer): FileDocumentViewer {
        const documentViewer = new FileDocumentViewer();
        documentViewer.id = fileViewer.viewerId;
        documentViewer.type = fileViewer.type === "user" ? 'user' : 'bubble';
        return documentViewer;
    }
}

export interface FileDocument {

    /**
     * The document identifier
     */
    id: string;

    /**
     * The document name
     */
    name: string;

    /**
     * The document size (in bytes)
     */
    size: number;

    /**
     * The document date
     */
    date: Date;

    /**
     * The document media type (also known as a Multipurpose Internet Mail Extensions or MIME type) indicates the nature and format
     * of a document. MIME types are defined and standardized in IETF's RFC 6838
     */
    mime: string;

    /**
     * The document current state
     */
    state: FileDocumentState;

    /**
     * To ensure the security of your data, every document uploaded to Rainbow will be scanned for viruses.
     * This field reflects the security status of the document.
     */
    securityStatus: string;

    /**
     * Document thumbnail information (possible, available, url)
     */
    thumbnail: FileDocumentThumbnail;

    /**
     * When a large document is uploaded, it is sent to the backend in chunks, so its progress can be tracked.
     * This progress index (in %) is updated when a document is transferred between the client and the backend
     * (see event "ON_TRANSFERT_PROGRESS_UPDATED").
     */
    transfertProgress: number;

    /**
     * Direct access to underlying fileDescriptor (to compatibility mode)
     * @internal
     */
    fileDescriptor: FileDescriptor;

    /**
     * This method makes it possible to subscribe to the document events (all events are of RBEvent);
     * @param handler - The call-back function that will be subscribed to the RxJS subject
     * @param eventNames - array of event to listen
     */
    subscribe(handler: (event: RBEvent<FileDocumentEvents>) => any, eventNames?: FileDocumentEvents | FileDocumentEvents[]): Subscription;

    /**
     * Get this document owner user
     */
    getOwner(): Promise<User>;

    /**
     * Replace the document content.
     * @param data - the new content blob
     */
    replaceContent(data: Blob): void;

    /**
     * Uploads a document to the file server.
     * It is possible to interrupt the upload of a document using the `abortDocumentTransfer` method.
     */
    upload(): Promise<FileDocument>;

    /**
     * Download a document
     * This method returns a blob that you can process as you see fit.
     * It is possible to interrupt the download of a document using the `abortDocumentTransfer` method.
     */
    download(): Promise<Blob>;

    /**
     * This method allows you to copy a document shared by another user into your own storage space.
     */
    copy(): Promise<FileDocument>;

    /**
     * Abort a document transfert
     * This method allows you to interrupt a transfer operation (upload or download).
     */
    abortTransfert(): Promise<FileDocument>;

    /**
     * Delete a document
     */
    delete(): Promise<void>;

    /**
     * Add a viewer to share the this document.
     * @param viewer - The viewer to be added, which can be either a `User` or a `Bubble` instance.
     */
    addViewer(viewer: User | Bubble): Promise<void>;

    /**
     * Remove a viewer
     * @param viewer - The viewer to be removed, which can be either a `User` or a `Bubble` instance.
     */
    removeViewer(viewer: User | Bubble): Promise<void>;

    /**
     * Return a local URL for this fileDocument (this url can be only used locally by the connectedUser)
     */
    getLocalUrl(): Promise<string>;

    /**
     * Return a temporary public URL for this fileDocument (this url can be shared and use by everyone (TTL = 1 hour))
     */
    getPublicUrl(): Promise<string>;

    /**
     * Return the text content of a file document
     */
    getTextContent(noCache?: boolean): Promise<string>;

    /**
     * Return the json content of a file document, throw an exception if typeMime is not ''
     */
    getJsonContent<T = any>(): Promise<T>;

    /**
     * Utility method for generating a human readable JSON representation of this object (debug purpose only)
     */
    toJSON(): any;
}

/** @internal */
export class FileDocumentRB implements FileDocument {

    private documentService: any = MainService.getInstance().inject<FileDocumentService>(FILEDOCUMENT_SVC);
    private contactService: ContactService = ContactService.getInstance();
    private logger: LoggerService = LoggerService.getInstance();

    public fd: FileDescriptor;
    private eventSubject: Subject<RBEvent<FileDocumentEvents>> = new Subject<RBEvent<FileDocumentEvents>>();
    private thumbnailSubscription?: Subscription;
    public transfertProgressSubscription?: Subscription;
    private transfertProgressValue: number = -1;

    public get id(): string { return this.fd.id; }
    public get name(): string { return this.fd.fileName; }
    public get date(): Date { return this.fd.getDate(); }
    public get size(): number { return this.fd.size; }
    public get mime(): string { return this.fd.typeMIME; }
    public get state(): FileDocumentState { return this.fd.state as unknown as FileDocumentState; }
    public get securityStatus(): string { return this.fd.virusScanState; };
    public get fileDescriptor(): FileDescriptor { return this.fd; };
    public get transfertProgress(): number { return this.fd.chunkPerformedPercent; }
    public get viewers(): FileDocumentViewer[] { return this.fd.viewers.map(viewer => FileDocumentViewer.createFormFileViewer(viewer)); }

    public thumbnail: FileDocumentThumbnail;

    public subscribe(handler: (event: RBEvent<FileDocumentEvents>) => any, eventNames?: FileDocumentEvents | FileDocumentEvents[]): Subscription {
        const observable = eventNames
            ? this.eventSubject.pipe(filter((event: RBEvent<FileDocumentEvents>) => {
                if (eventNames instanceof Array) return eventNames.indexOf(event.name) !== -1;
                return event.name === eventNames;
            }))
            : this.eventSubject;

        return observable.subscribe((event: RBEvent<FileDocumentEvents>) => {
            try { handler(event); }
            catch (error) { this.logger.error(`[FileDocument] subscribe () -- error -- ${error.message}`); }
        });
    }

    public sendEvent(name: FileDocumentEvents, data: any = null) {
        this.eventSubject.next(RBEvent.create<FileDocumentEvents, any>(name, data));
    }

    public async getOwner(): Promise<User> {
        return this.contactService.getContactByDBId(this.fd.ownerId);
    }

    public replaceContent(data: Blob): void {
        this.documentService?.replaceFileDocumentContent(this, data);
    }

    public async upload(): Promise<FileDocument> {
        return await this.documentService?.uploadDocument(this);
    }

    public async download(): Promise<Blob> {
        return await this.documentService?.downloadDocument(this);
    }

    public async copy(): Promise<FileDocument> {
        return await this.documentService?.copyDocument(this);
    }

    public async abortTransfert(): Promise<FileDocument> {
        return await this.documentService?.abortDocumentTransfert(this);
    }

    public async delete(): Promise<void> {
        return await this.documentService?.deleteDocument(this);
    }

    public hasEnoughSpace(): boolean {
        return this.documentService?.hasEnoughSpace(this);
    }

    public async addViewer(viewer: User | Bubble): Promise<void> {
        return await this.documentService?.addDocumentViewer(this, viewer);
    }

    public async removeViewer(viewer: User | Bubble): Promise<void> {
        return await this.documentService?.deleteDocumentViewer(this, viewer);
    }

    public async getLocalUrl(): Promise<string> {
        return await this.documentService?.getLocalUrl(this);
    }

    public async getPublicUrl(): Promise<string> {
        return await this.documentService?.getPublicUrl(this);
    }

    public async getTextContent(noCache: boolean = false): Promise<string> {
        return await this.documentService?.getTextContent(this, noCache);
    }

    public async getJsonContent<T = any>(): Promise<T> {
        const service = this.documentService as { getJsonContent<T = any>(doc: unknown): Promise<T>; };
        return await service?.getJsonContent<T>(this);
    }

    public toJSON(): any {
        const json: any = {
            id: this.id,
            name: this.name,
            date: this.date,
            size: this.size,
            mime: this.mime,
            state: this.state,
            owner: this.fd.ownerId,
            securityStatus: this.securityStatus,
            thumbnail: this.thumbnail,
        };
        if (this.viewers.length) json.viewers = this.viewers;
        return json;
    }

    public handleThumbnail(): void {
        // Handle thumbnail info
        this.thumbnail = {
            possible: this.fd.isThumbnailPossible(),
            available: this.fd.thumbnail.availableThumbnail,
            url: () => this.documentService?.getFileDescriptorUrl(this.fd, true, true)
        };

        // Listen once on availability
        if (this.thumbnail.possible && !this.thumbnail.available) {
            this.thumbnailSubscription = this.fd.previewBlobSubject.subscribe(() => {
                this.thumbnail.available = this.fd.previewBlobSubject.value !== undefined;
                this.sendEvent(FileDocumentEvents.ON_THUMBNAIL_AVAILABLE);
                this.thumbnailSubscription?.unsubscribe();
            });
        }
    }

    public handleTransferProgress(): void {
        this.transfertProgressValue = -1;
        this.transfertProgressSubscription = this.fd.progressSubject.subscribe(() => {

            // Handle only new transfert progress values
            if (this.fd.chunkPerformedPercent > this.transfertProgressValue) {

                // Send Progress event
                this.sendEvent(FileDocumentEvents.ON_TRANSFERT_IN_PROGRESS, this.fd.chunkPerformedPercent);
                this.transfertProgressValue = this.fd.chunkPerformedPercent;

                // Remove subscription when transfert is finnished
                if (this.fd.chunkPerformedPercent >= 100) {
                    this.transfertProgressSubscription.unsubscribe();
                    this.transfertProgressSubscription = undefined;
                }
            }
        });
    }

}
